import { IFileResponse } from '@api/client';
import { FileTypes } from '@api/extended-client';
import { isImageByExtension, isVideoByExtension } from '@utils/media-file.helpers';
import { getVideoThumbnailUrl } from '@utils/video-thumbnail-url';
import { getImageThumbnailUrl, ImagePreviewSize } from '@utils/image-url.utils';

const PLAYLIST_EXTENSIONS: ReadonlySet<string> = new Set<string>([
  'm3u',
  'm3u8',
]);

const PLAYLIST_MIME_TYPES: ReadonlySet<string> = new Set<string>([
  'application/vnd.apple.mpegurl',
  'application/x-mpegurl',
  'audio/mpegurl',
  'audio/x-mpegurl',
]);

type FileMediaMatcher = {
  typeId: number;
  typeName: string;
  mimePrefix: string;
  extensionMatcher: (value: string) => boolean;
};

function normalizeFileValue(value: string | null | undefined): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmedValue = value.trim();
  return trimmedValue.length > 0 ? trimmedValue : null;
}

function matchesFileMediaType(
  file: IFileResponse | null | undefined,
  matcher: FileMediaMatcher
): boolean {
  if (!file) {
    return false;
  }

  const typeValue = file.type;
  if (typeValue) {
    if (typeValue.id === matcher.typeId) {
      return true;
    }

    if (typeof typeValue.name === 'string') {
      const normalizedName = typeValue.name.toLowerCase();
      if (normalizedName === matcher.typeName || normalizedName.startsWith(matcher.mimePrefix)) {
        return true;
      }
    }
  }

  const fileUrl = normalizeFileValue(file.url) ?? '';
  const fileName = normalizeFileValue(file.name) ?? '';
  return matcher.extensionMatcher(fileUrl) || matcher.extensionMatcher(fileName);
}

function appendMetadataFragment(url: string): string {
  if (!url || url.includes('#')) {
    return url;
  }

  return `${url}#t=0.001`;
}

function extractExtension(value: string | null | undefined): string | null {
  const normalizedValue = normalizeFileValue(value);
  if (!normalizedValue) {
    return null;
  }

  const cleanValue = normalizedValue.split('#')[0].split('?')[0];
  const fileName = cleanValue.split('/').pop()?.trim() ?? '';
  const dotIndex = fileName.lastIndexOf('.');
  if (dotIndex < 0 || dotIndex === fileName.length - 1) {
    return null;
  }

  const extension = fileName.slice(dotIndex + 1).trim().toLowerCase();
  return extension || null;
}

function isPlaylistFile(file: IFileResponse | null | undefined): boolean {
  if (!file) {
    return false;
  }

  const typeName = normalizeFileValue(file.type?.name)?.toLowerCase();
  if (typeName && PLAYLIST_MIME_TYPES.has(typeName)) {
    return true;
  }

  const urlExtension = extractExtension(file.url);
  if (urlExtension && PLAYLIST_EXTENSIONS.has(urlExtension)) {
    return true;
  }

  const nameExtension = extractExtension(file.name);
  return !!nameExtension && PLAYLIST_EXTENSIONS.has(nameExtension);
}

export function isFileVideo(file: IFileResponse | null | undefined): boolean {
  if (isPlaylistFile(file)) {
    return false;
  }

  return matchesFileMediaType(file, {
    typeId: FileTypes.TYPE_VIDEO,
    typeName: 'video',
    mimePrefix: 'video/',
    extensionMatcher: isVideoByExtension,
  });
}

export function isFileImage(file: IFileResponse | null | undefined): boolean {
  if (file?.type?.id === FileTypes.TYPE_AVATAR) {
    return true;
  }

  return matchesFileMediaType(file, {
    typeId: FileTypes.TYPE_IMAGE,
    typeName: 'image',
    mimePrefix: 'image/',
    extensionMatcher: isImageByExtension,
  });
}

export function isFileMedia(file: IFileResponse | null | undefined): boolean {
  return isFileVideo(file) || isFileImage(file);
}

export function getFileImagePreviewUrl(
  file: IFileResponse | null | undefined,
  targetSize: number = ImagePreviewSize.Thumbnail
): string | null {
  return getImageThumbnailUrl(file, targetSize);
}

export function getFileVideoPreviewUrl(file: IFileResponse | null | undefined): string | null {
  return getVideoThumbnailUrl(file);
}

export function getFileVideoFallbackUrl(
  file: IFileResponse | null | undefined,
  isDevice: boolean
): string | null {
  if (!file || isDevice) {
    return null;
  }

  const directUrl = normalizeFileValue(file.url);
  if (!directUrl) {
    return null;
  }

  return appendMetadataFragment(directUrl);
}
